import { useEffect, useState, useRef } from "react";
import CheckBox from "../components/CheckBox";
import { focusElement } from "../js/utils";
import "../css/InputData.css";
import "../css/InputIconSearch.css";
import "../css/InputIconSearchPanel.css";
import "../css/InputIconSearchPanelMultiSelection.css";

export default function InputIconSearchPanelMultiSelection(props) {
  const [togglePanel, setTogglePanel] = useState(false);
  const [optionSelected, setOptionSelected] = useState(props.selectedOptions);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputSearch, setInputSearch] = useState("");
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);
  const inputSearchRef = useRef("");

  const getInputSize = () => {
    const sizeClass = {
      0: "input-full",
      1: "input-small",
      2: "input-medium",
      3: "input-large",
      4: "input-super-small",
    };
    return sizeClass[props.size] || "";
  };

  const searchOptions = () => {
    if (inputSearch === optionSelected) {
      setTogglePanel(false);
    }
  };

  const getSearchMatches = () => {
    const currentSearch = inputSearchRef.current;
    if (currentSearch !== "") {
      return props.options.filter((e) => e.includes(currentSearch)).length;
    }
    return 0;
  };

  useEffect(() => {
    if (props.inactive) {
      setTogglePanel(false);
    }
  }, [props.inactive]);

  useEffect(() => {
    searchOptions();
  }, [inputSearch]);

  useEffect(() => {
    setOptionSelected(props.selectedOptions);
  }, [props.selectedOptions]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!togglePanel) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex < props.options.length - 1 ? prevIndex + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : props.options.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const currentSearch = inputSearchRef.current;

        const matchedOptions = props.options.filter((opt) =>
          opt.includes(currentSearch)
        );

        if (currentSearch !== "" && matchedOptions.length > 0) {
          matchedOptions.forEach((match) => {
            handleSelectOption(match);
          });
          return;
        }

        if (highlightedIndex >= 0 && highlightedIndex < props.options.length) {
          const selectedOption = props.options[highlightedIndex];
          handleSelectOption(selectedOption);
          return;
        }
      } else if (e.key === "Escape") {
        setTogglePanel(false);
      }
    };

    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setTogglePanel(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [togglePanel, highlightedIndex, props.options]);

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      const highlightedOption = optionRefs.current[highlightedIndex];
      const panel = panelRef.current;
      if (panel && highlightedOption) {
        const optionTop = highlightedOption.offsetTop;
        const optionBottom = optionTop + highlightedOption.clientHeight;
        const panelTop = panel.scrollTop;
        const panelBottom = panelTop + panel.clientHeight;

        if (optionTop < panelTop) {
          panel.scrollTop = optionTop;
        } else if (optionBottom > panelBottom) {
          panel.scrollTop = optionBottom - panel.clientHeight;
        }
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    setInputSearch("");
    setOptionSelected(props.selectedOptions);
  }, [props.options]);

  const handleSelectOption = (selectedOption) => {
    setOptionSelected((prev) =>
      !prev.includes(selectedOption)
        ? [...prev, selectedOption]
        : prev.filter((brand) => brand !== selectedOption)
    );
    props.onChange((prev) =>
      !prev.includes(selectedOption)
        ? [...prev, selectedOption]
        : prev.filter((brand) => brand !== selectedOption)
    );
  };

  return (
    <div className="input-search-panel-container">
      <div
        className={`input-data input-icon-search ${getInputSize()} ${
          props.inactive ? "input-search-inactive" : ""
        }`}
        ref={inputRef}
      >
        <img
          src="/images/search-blue.svg"
          loading="lazy"
          width="14"
          height="14"
          alt="icon"
          className="input-icon-search-img"
        />
        <input
          id={props.id}
          tabIndex={props.tabIndex}
          type={props.type ? props.type : "text"}
          disabled={props.inactive}
          placeholder={props.placeholder}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            inputSearchRef.current = value;
            setInputSearch(value);

            const indexMatch = props.options.findIndex((opt) =>
              opt.includes(value)
            );
            setHighlightedIndex(indexMatch >= 0 ? indexMatch : -1);
          }}
          onClick={() => {
            setTogglePanel(true);
          }}
          value={inputSearch}
        ></input>
        {inputSearch !== "" && (
          <img
            src="/images/close-dark.svg"
            loading="lazy"
            width="14"
            height="14"
            alt="icon"
            className="input-icon-search-clear"
            onClick={() => {
              setInputSearch("");
              setHighlightedIndex(-1);
            }}
          />
        )}
        <img
          src="/images/list-blue.svg"
          loading="lazy"
          width="14"
          height="14"
          alt="icon"
          className="input-icon-search-clear"
          style={{ marginLeft: "10px" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!props.inactive) {
              setTogglePanel((prevToggle) => !prevToggle);
              setHighlightedIndex(-1);
              setInputSearch("");
              focusElement(props.tabIndex);
            }
          }}
        />
      </div>
      {togglePanel && (
        <div className="input-search-panel" ref={panelRef}>
          <div className="input-search-panel-header">
            <span style={{ fontWeight: "500" }}>
              Coincidencias: {getSearchMatches()}
            </span>
            <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
              <button
                style={{
                  color: `var(--dark-blue)`,
                  fontWeight: 500,
                  cursor: "pointer",
                  right: "0px",
                }}
                onClick={() => {
                  setOptionSelected(props.options);
                  props.onChange(props.options);
                }}
                tabIndex={-1}
              >
                Seleccionar todo
              </button>
              /
              <button
                style={{
                  color: `var(--dark-blue)`,
                  fontWeight: 500,
                  cursor: "pointer",
                  right: "0px",
                }}
                onClick={() => {
                  setOptionSelected([]);
                  props.onChange([]);
                }}
                tabIndex={-1}
              >
                Limpiar selección
              </button>
            </div>
          </div>
          {props.options.map((e, i) => {
            return (
              <div
                className={`search-result-info ${
                  highlightedIndex === i ||
                  (e.includes(inputSearch) && inputSearch !== "")
                    ? "search-result-info-selected"
                    : ""
                }`}
                style={{ justifyContent: "flex-start", gap: "8px" }}
                key={i}
                ref={(el) => (optionRefs.current[i] = el)}
                onClick={() => {
                  handleSelectOption(e);
                }}
              >
                <CheckBox
                  active={optionSelected.includes(e)}
                  onClick={() => {}}
                ></CheckBox>
                {e}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
